// ----------------------------------------------------------------------------
// Centralized analytics helper — every dataLayer push goes through trackEvent
// so we get: (a) a single consent gate, (b) typed event names that match the
// GTM dashboard, (c) a single place to add custom dimensions like page_type.
//
// The site's GoogleTagManagerScript only mounts GTM AFTER the user accepts
// the analytics cookie (see /src/components/layouts/GoogleTagManagerScript.tsx).
// trackEvent mirrors that gate so we never push events with no listener.
// ----------------------------------------------------------------------------

import { sendGTMEvent } from "@next/third-parties/google";

// ----------------------------------------------------------------------------
// Bot / headless-visitor filter. GA4 already drops known IAB bots, but it
// doesn't filter automation tools (Playwright/Selenium/Puppeteer), Lighthouse
// audits, security scanners, or scripted scrapers. Without this guard our
// conversion counts get polluted by:
//   - Lighthouse / PageSpeed runs (we run them ourselves)
//   - LinkedIn / Slack / WhatsApp link-preview crawlers
//   - AI scrapers that DO execute JS (GPTBot et al.)
//   - Internal QA runs via headless Chrome
// All checks are belt-and-suspenders; any single positive → drop the event.
// ----------------------------------------------------------------------------

const BOT_UA_PATTERNS: readonly RegExp[] = [
  // Search-engine crawlers
  /googlebot|adsbot-google|google-inspectiontool|mediapartners-google/i,
  /bingbot|msnbot/i,
  /yandex(bot|images)/i,
  /baiduspider|duckduckbot|slurp/i,
  // Social link-preview crawlers
  /facebookexternalhit|facebot|twitterbot|linkedinbot|slackbot|whatsapp|telegrambot|discordbot/i,
  // AI scrapers
  /gptbot|chatgpt-user|oai-searchbot|claude-?(web|bot)|anthropic-ai|perplexitybot|google-extended|ccbot|bytespider|amazonbot|applebot/i,
  // Headless / automated browsers
  /headlesschrome|phantomjs|electron|jsdom/i,
  /lighthouse|pagespeed|gtmetrix|webpagetest|chrome-?headless/i,
  /selenium|puppeteer|playwright|cypress|webdriver|nightmare/i,
  // Security scanners
  /nuclei|burp|nessus|qualys|acunetix|netsparker|nmap|sqlmap|nikto|wpscan|zap\b|w3af|sucuri|detectify/i,
  // Monitoring / synthetic
  /uptimerobot|pingdom|statuscake|monitis|site24x7|datadogsynthetics|new-?relic/i,
  // Generic catch-alls — last so specific names match first
  /\b(bot|crawler|spider|scraper|monitor)\b/i,
];

const hasHeadlessGlobals = (): boolean => {
  const w = window as unknown as Record<string, unknown>;
  return Boolean(
    w.__playwright ||
    w.__pw_manual ||
    w.__selenium_unwrapped ||
    w.__webdriver_evaluate ||
    w.callPhantom ||
    w._phantom ||
    w.__nightmare ||
    w.spawn || // RhinoCSS / RhinoScript
    w.emit, // some headless browsers
  );
};

const isBot = (): boolean => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return true; // SSR — never count
  }

  // 1. The smoking gun: WebDriver flag set by every major automation tool.
  if ((navigator as Navigator & { webdriver?: boolean }).webdriver) return true;

  // 2. Headless-specific global properties.
  if (hasHeadlessGlobals()) return true;

  // 3. User-agent fingerprinting (catches scrapers + crawlers that honour UA).
  const ua = navigator.userAgent || "";
  if (!ua) return true; // no UA → almost certainly a bot
  for (const pattern of BOT_UA_PATTERNS) {
    if (pattern.test(ua)) return true;
  }

  // 4. Manual opt-out for internal QA / dev — set
  //    `localStorage.setItem("disable_analytics","true")` in any browser
  //    you want excluded (your own machines, staging).
  try {
    if (window.localStorage?.getItem("disable_analytics") === "true") {
      return true;
    }
  } catch {
    // localStorage may be blocked in some contexts; ignore.
  }

  return false;
};

// Canonical event names — mirror what's configured in GTM as triggers.
// Adding a new event? Add it here first, then create the matching tag/trigger
// in GTM and mark it as a conversion in GA4 if appropriate.
export type AnalyticsEvent =
  // ---- THE conversion event -------------------------------------------
  // generate_lead is the GA4 key event imported into Google Ads as the
  // single primary conversion. It fires exactly ONCE per successful lead,
  // only after the backend/widget confirms success, and always carries:
  //   lead_source / form_id: pentest_quote | sample_report
  //                        | contact_form  | discovery_call
  // Segment by lead_source in GA4 rather than adding new event names.
  | "generate_lead"
  // ---- GA4-only segmentation ------------------------------------------
  // These fire ALONGSIDE generate_lead for funnel reporting. Never build a
  // Google Ads tag on them — that would double-count every lead.
  | "quote_request" // pentest/VAPT quote submitted (high intent)
  | "sample_report_request" // sample-report submitted (mid intent)
  | "book_discovery_call" // Calendly booking confirmed (highest intent)
  | "cta_click" // any CTAButton click
  | "download_resource" // PDF / case study / sample report click
  | "phone_click" // tel: link click
  | "email_click" // mailto: link click
  | "start_assessment" // security assessment CTA click
  | "nav_click" // ActionButton click — navigational, NOT a conversion
  // Tier 2 — engagement (signals lead quality / topic interest)
  | "faq_open" // FAQ accordion item expand
  | "tab_change" // industry/service tab switch
  | "mega_menu_open" // mega-menu hover (intent signal)
  | "view_service_item" // service page mount
  | "view_industry_item" // industry page mount
  // Tier 3 — form funnel
  | "form_start" // first interaction with a form field
  | "form_field_error" // yup/RHF validation error on a field
  | "form_abandon" // user left the page without submitting
  | "begin_checkout" // contact form start (GA4 ecommerce alias)
  | "view_item" // service/industry page (GA4 ecommerce alias)
  // Tier 5 — performance / health
  | "web_vital"; // Core Web Vital metric report (LCP/CLS/INP/FCP/TTFB)

// Infers a page_type custom dimension from the current pathname so every
// event is automatically segmentable by surface in GA4 without each call
// site having to pass it explicitly.
//   /                     → home
//   /services/:slug       → service
//   /industries/:slug     → industry
//   /resources/...        → resource
//   /blog/... | /blogs/...→ blog
//   /contact-us           → contact
//   /about-us             → about
//   /                     → home (default)
export const inferPageType = (pathname: string): string => {
  if (!pathname || pathname === "/") return "home";
  if (pathname.startsWith("/services")) return "service";
  if (pathname.startsWith("/industries")) return "industry";
  if (pathname.startsWith("/resources")) return "resource";
  if (pathname.startsWith("/blog")) return "blog";
  if (pathname.startsWith("/contact")) return "contact";
  if (pathname.startsWith("/about")) return "about";
  if (pathname.startsWith("/schedule")) return "schedule";
  if (pathname.startsWith("/take-security-assessment")) return "assessment";
  return "other";
};

// Pulls a slug-ish identifier from common page paths so industry / service
// dashboards can filter by topic without an extra event property at each
// call site.
export const inferContextSlug = (pathname: string): string | undefined => {
  const m = pathname.match(
    /^\/(?:services|industries|resources|blog)\/([^/?#]+)/,
  );
  return m?.[1];
};

type EventParams = Record<string, string | number | boolean | undefined>;

export const trackEvent = (event: AnalyticsEvent, params: EventParams = {}) => {
  // Bot / headless filter — drop scanners, automation, AI scrapers, etc.
  // See isBot() for the full check list.
  if (isBot()) return;

  // With Consent Mode v2 (see GoogleTagManagerScript.tsx) GTM is always
  // mounted and GA4 handles consent state via cookieless modeling. We
  // intentionally DO NOT block events here on the consent cookie — denied
  // users still contribute modeled data points that GA4 uses to recover
  // ~15-40% of attributed conversions per Google's own benchmarks.
  if (typeof window === "undefined") return;

  const pathname = window.location?.pathname || "";

  // Strip undefined values so they don't pollute the dataLayer.
  const cleanParams: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) cleanParams[k] = v;
  }

  sendGTMEvent({
    event,
    page_type: inferPageType(pathname),
    context_slug: inferContextSlug(pathname),
    ...cleanParams,
  });
};

// ----------------------------------------------------------------------------
// User properties — long-lived attributes (sticky across sessions) that
// segment cohorts in GA4 Explore. Stored in localStorage so they persist
// across visits. Pushed to GTM as a "set_user_properties" event so GA4
// associates them with all subsequent events in the session.
// ----------------------------------------------------------------------------

export type UserProperties = {
  user_type?: "new" | "returning" | "lead" | "multi_lead";
  industry_interest?: string; // most-recently-visited industry slug
  service_interest?: string; // most-recently-visited service slug
  traffic_origin?: string; // first-touch source (sticky after first hit)
};

const USER_PROPS_KEY = "cb_user_props";
const FIRST_VISIT_KEY = "cb_first_visit_at";

const readProps = (): UserProperties => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage?.getItem(USER_PROPS_KEY);
    return raw ? (JSON.parse(raw) as UserProperties) : {};
  } catch {
    return {};
  }
};

const writeProps = (props: UserProperties) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage?.setItem(USER_PROPS_KEY, JSON.stringify(props));
  } catch {
    // ignore quota / disabled storage
  }
};

export const setUserProperties = (patch: Partial<UserProperties>) => {
  if (isBot()) return;
  if (typeof window === "undefined") return;
  // No consent gate — localStorage writes are functionality_storage
  // (always granted) and GTM/GA4 honors consent state for the push.
  const next = { ...readProps(), ...patch };
  writeProps(next);
  sendGTMEvent({ event: "set_user_properties", ...next });
};

// Derives traffic origin from referrer + UTM params on first visit only,
// then sticks for the lifetime of localStorage. First-touch attribution
// beats last-touch for B2B because the buyer journey is multi-session.
const captureFirstTouchOrigin = () => {
  const props = readProps();
  if (props.traffic_origin) return; // already captured
  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get("utm_source");
  let origin = "direct";
  if (utmSource) {
    origin = `paid:${utmSource.toLowerCase().slice(0, 30)}`;
  } else if (document.referrer) {
    try {
      const refHost = new URL(document.referrer).hostname;
      if (refHost.includes("google.")) origin = "organic:google";
      else if (refHost.includes("bing.")) origin = "organic:bing";
      else if (refHost.includes("duckduckgo.")) origin = "organic:duckduckgo";
      else if (refHost.includes("linkedin.")) origin = "social:linkedin";
      else if (refHost.includes("twitter.") || refHost.includes("x.com"))
        origin = "social:twitter";
      else if (refHost.includes("facebook.")) origin = "social:facebook";
      else if (refHost.includes("chat.openai.") || refHost.includes("chatgpt."))
        origin = "ai:chatgpt";
      else if (refHost.includes("perplexity.")) origin = "ai:perplexity";
      else if (refHost.includes("claude.")) origin = "ai:claude";
      else if (!refHost.includes(window.location.hostname))
        origin = `referral:${refHost.replace(/^www\./, "").slice(0, 30)}`;
    } catch {
      // bad referrer; keep default
    }
  }
  setUserProperties({ traffic_origin: origin });
};

// Determines new vs returning by checking the first-visit marker.
const captureUserType = () => {
  if (typeof window === "undefined") return;
  let type: UserProperties["user_type"] = "returning";
  try {
    if (!window.localStorage?.getItem(FIRST_VISIT_KEY)) {
      window.localStorage?.setItem(FIRST_VISIT_KEY, String(Date.now()));
      type = "new";
    }
  } catch {
    return;
  }
  const existing = readProps();
  // Don't downgrade lead/multi_lead users back to "returning"
  if (existing.user_type === "lead" || existing.user_type === "multi_lead") {
    return;
  }
  setUserProperties({ user_type: type });
};

// Called from contact-form submission to upgrade the user_type.
export const markUserAsLead = () => {
  if (isBot()) return;
  const existing = readProps();
  const next: UserProperties["user_type"] =
    existing.user_type === "lead" ? "multi_lead" : "lead";
  setUserProperties({ user_type: next });
};

// Bootstraps user properties on session start. Safe to call multiple times
// (no-ops after the first run per session). No consent gate — see
// setUserProperties for rationale.
export const initUserSession = () => {
  if (isBot()) return;
  if (typeof window === "undefined") return;
  captureFirstTouchOrigin();
  captureUserType();
};

// Called from industry/service page mount to update the interest slug.
// Most-recent-wins (simple and effective for narrow funnels like ours).
export const recordInterest = (kind: "industry" | "service", slug: string) => {
  if (!slug) return;
  setUserProperties(
    kind === "industry"
      ? { industry_interest: slug }
      : { service_interest: slug },
  );
};
